import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MDSpinner from "react-md-spinner";
import GoogleMapReact from "google-map-react";
import axios from "axios";
import {
  FaCheck,
  FaDrawPolygon,
  FaMapMarkedAlt,
  FaMapPin,
  FaPen,
  FaPlus,
  FaTimes,
  FaTrashAlt,
  FaUndo,
} from "react-icons/fa";
import { toast } from "react-toastify";

import MapViewportToolbar from "../../components/MapViewportToolbar";
import myConfig from "../../configs/config";
import {
  MAP_VIEWPORT_SCOPES,
  applyViewportToGoogleMap,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  resolveMapViewport,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import {
  computePointsAreaHa,
  computePointsPerimeterM,
  getLandPlotMapStyle,
  getPlotMapLabel,
  manualPieceColor,
  parseLandplotRing,
  pathToPolygonWkt,
  summarizeManualApplicationPieces,
} from "../../utils/mapMeasurements";
import { fetchGroupLandPlots } from "./serviceOrderLocationApi";
import { calculateDistance, getMapOptions } from "./rumoPistaUtils";
import { useResizablePanel } from "./useResizablePanel";
import ServiceOrderMapOverlay from "./ServiceOrderMapOverlay";
import { releaseModalBlockers } from "../../utils/nestedMuiModalProps";
import "./ServiceOrderLocationMapModal.css";
import "./TrackHeadingMapModal.css";

const DRAW_MODES = {
  POINTS: "points",
  PENCIL: "pencil",
};

const PENCIL_MIN_DISTANCE_M = 2;

function createPieceId() {
  return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function shouldAppendPencilPoint(points, nextPoint) {
  if (!points.length) return true;
  const lastPoint = points[points.length - 1];
  return (
    calculateDistance(
      lastPoint.lat,
      lastPoint.lng,
      nextPoint.lat,
      nextPoint.lng
    ) >= PENCIL_MIN_DISTANCE_M
  );
}

function buildPieceFromDraft(points, maps, index) {
  return {
    id: createPieceId(),
    label: `Trecho ${index + 1}`,
    points: points.map((point) => ({ ...point })),
    areaHa: computePointsAreaHa(points, maps),
    perimeterM: computePointsPerimeterM(points, maps),
    polygonWkt: pathToPolygonWkt(points),
  };
}

export default function ServiceOrderManualApplicationMapModal({
  open,
  farmId,
  onClose,
  onConfirm,
  initialManualPieces,
  t,
}) {
  const [loading, setLoading] = useState(true);
  const [landPlots, setLandPlots] = useState([]);
  const [farmCenter, setFarmCenter] = useState(null);
  const [savedMapViewport, setSavedMapViewport] = useState(null);
  const [draftPoints, setDraftPoints] = useState([]);
  const [drawMode, setDrawMode] = useState(DRAW_MODES.POINTS);
  const [savedPieces, setSavedPieces] = useState([]);
  const [mapReady, setMapReady] = useState(false);

  const mapApiRef = useRef(null);
  const overlaysRef = useRef([]);
  const mapClickListenerRef = useRef(null);
  const mapDrawListenersRef = useRef([]);
  const isPencilDrawingRef = useRef(false);
  const pencilWindowMouseUpRef = useRef(null);

  const { rect, ResizeHandles } = useResizablePanel(open);

  useEffect(() => {
    if (!open || !farmId) return;

    setLoading(true);
    setDraftPoints([]);
    setDrawMode(DRAW_MODES.POINTS);
    setSavedPieces(
      Array.isArray(initialManualPieces) ? initialManualPieces.map((piece) => ({
        ...piece,
        points: Array.isArray(piece.points) ? piece.points.map((point) => ({ ...point })) : [],
      })) : []
    );
    setMapReady(false);

    Promise.all([
      axios.get(`${myConfig.API_URL}/farm/${farmId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      }),
      fetchGroupLandPlots(farmId),
    ])
      .then(([farmRes, plots]) => {
        setFarmCenter(farmRes.data.farm_center);
        setSavedMapViewport(farmRes.data.map_viewport || null);
        setLandPlots(plots);
      })
      .catch(() => {
        setFarmCenter(null);
        setLandPlots([]);
      })
      .finally(() => setLoading(false));
  }, [open, farmId, initialManualPieces]);

  const initialMapViewport = useMemo(() => {
    if (!farmCenter) return null;
    return resolveMapViewport({
      apiViewport: savedMapViewport,
      localViewport: readLocalMapViewport(
        MAP_VIEWPORT_SCOPES.SERVICE_ORDER_MANUAL_APPLICATION,
        farmId
      ),
      fallbackCenter: farmCenter,
      fallbackZoom: 16,
    });
  }, [farmCenter, savedMapViewport, farmId]);

  const draftMetrics = useMemo(() => {
    if (draftPoints.length < 3) return null;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    return {
      areaHa: computePointsAreaHa(draftPoints, maps),
      perimeterM: computePointsPerimeterM(draftPoints, maps),
    };
  }, [draftPoints, mapReady]);

  const piecesSummary = useMemo(
    () => summarizeManualApplicationPieces(savedPieces),
    [savedPieces]
  );

  const clearOverlays = useCallback(() => {
    overlaysRef.current.forEach((overlay) => {
      if (overlay && overlay.setMap) overlay.setMap(null);
    });
    overlaysRef.current = [];
  }, []);

  const detachMapClick = useCallback(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (maps && maps.event && mapClickListenerRef.current) {
      maps.event.removeListener(mapClickListenerRef.current);
    }
    mapClickListenerRef.current = null;
  }, []);

  const detachPencilDraw = useCallback(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    const map = mapApiRef.current && mapApiRef.current.map;
    if (maps && maps.event) {
      mapDrawListenersRef.current.forEach((listener) => {
        maps.event.removeListener(listener);
      });
    }
    mapDrawListenersRef.current = [];
    if (pencilWindowMouseUpRef.current) {
      window.removeEventListener("mouseup", pencilWindowMouseUpRef.current);
      pencilWindowMouseUpRef.current = null;
    }
    isPencilDrawingRef.current = false;
    if (map) {
      map.setOptions({ draggable: true });
    }
  }, []);

  const stopPencilDrawing = useCallback(() => {
    const map = mapApiRef.current && mapApiRef.current.map;
    isPencilDrawingRef.current = false;
    if (map) {
      map.setOptions({ draggable: true });
    }
  }, []);

  const renderMap = useCallback(() => {
    const map = mapApiRef.current && mapApiRef.current.map;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (!map || !maps) return;

    clearOverlays();

    landPlots.forEach((plot) => {
      const ring = parseLandplotRing(plot);
      if (ring.length < 3) return;
      const plotStyle = getLandPlotMapStyle(false);
      overlaysRef.current.push(
        new maps.Polygon({
          paths: ring,
          map,
          strokeColor: plotStyle.strokeColor,
          strokeOpacity: 0.35,
          strokeWeight: 1,
          fillColor: plotStyle.fillColor,
          fillOpacity: 0.08,
          zIndex: 1,
          clickable: false,
        })
      );

      if (plot.landplot_center && plot.landplot_center.lat != null) {
        overlaysRef.current.push(
          new maps.Marker({
            map,
            position: plot.landplot_center,
            label: {
              text: getPlotMapLabel(plot),
              color: "#607d8b",
              fontWeight: "600",
              fontSize: "10px",
            },
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 0,
              fillOpacity: 0,
              strokeOpacity: 0,
            },
            zIndex: 2,
            clickable: false,
          })
        );
      }
    });

    savedPieces.forEach((piece, index) => {
      const colors = manualPieceColor(index);
      const points = piece.points || [];
      if (points.length < 3) return;

      overlaysRef.current.push(
        new maps.Polygon({
          paths: points,
          map,
          strokeColor: colors.stroke,
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: colors.fill,
          fillOpacity: 0.35,
          zIndex: 4,
          clickable: false,
        })
      );
    });

    if (draftPoints.length > 0) {
      if (drawMode === DRAW_MODES.POINTS) {
        draftPoints.forEach((point, index) => {
          overlaysRef.current.push(
            new maps.Marker({
              position: point,
              map,
              label: {
                text: String(index + 1),
                color: "#ffffff",
                fontWeight: "bold",
              },
              icon: {
                path: maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: "#FF7043",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 2,
              },
              zIndex: 6,
            })
          );
        });
      }

      if (draftPoints.length >= 2) {
        overlaysRef.current.push(
          new maps.Polyline({
            path: draftPoints,
            map,
            strokeColor: "#FF7043",
            strokeOpacity: 0.95,
            strokeWeight: 3,
            zIndex: 5,
          })
        );
      }

      if (draftPoints.length >= 3) {
        overlaysRef.current.push(
          new maps.Polygon({
            paths: draftPoints,
            map,
            strokeColor: "#E64A19",
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: "#FF7043",
            fillOpacity: 0.2,
            zIndex: 4,
            clickable: false,
          })
        );
      }
    }
  }, [clearOverlays, draftPoints, drawMode, landPlots, savedPieces]);

  const attachDrawClick = useCallback(() => {
    detachMapClick();
    const map = mapApiRef.current && mapApiRef.current.map;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (!map || !maps) return;

    mapClickListenerRef.current = maps.event.addListener(map, "click", (event) => {
      const point = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setDraftPoints((current) => current.concat(point));
    });
  }, [detachMapClick]);

  const appendPencilPoint = useCallback((latLng) => {
    const point = {
      lat: latLng.lat(),
      lng: latLng.lng(),
    };
    setDraftPoints((current) =>
      shouldAppendPencilPoint(current, point) ? current.concat(point) : current
    );
  }, []);

  const attachPencilDraw = useCallback(() => {
    detachPencilDraw();
    const map = mapApiRef.current && mapApiRef.current.map;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (!map || !maps) return;

    const handleMouseDown = (event) => {
      if (event.domEvent && event.domEvent.button !== 0) return;
      isPencilDrawingRef.current = true;
      map.setOptions({ draggable: false });
      appendPencilPoint(event.latLng);
    };

    const handleMouseMove = (event) => {
      if (!isPencilDrawingRef.current) return;
      appendPencilPoint(event.latLng);
    };

    const handleMouseUp = () => {
      stopPencilDrawing();
    };

    mapDrawListenersRef.current = [
      maps.event.addListener(map, "mousedown", handleMouseDown),
      maps.event.addListener(map, "mousemove", handleMouseMove),
      maps.event.addListener(map, "mouseup", handleMouseUp),
    ];

    pencilWindowMouseUpRef.current = handleMouseUp;
    window.addEventListener("mouseup", handleMouseUp);
  }, [appendPencilPoint, detachPencilDraw, stopPencilDrawing]);

  useEffect(() => {
    const map = mapApiRef.current && mapApiRef.current.map;
    if (!open || loading || !mapReady || !map) return undefined;

    map.setOptions({ draggableCursor: "crosshair", draggingCursor: "crosshair" });
    return () => {
      map.setOptions({ draggableCursor: null, draggingCursor: null, draggable: true });
    };
  }, [open, loading, mapReady]);

  useEffect(() => {
    if (!open || loading || !mapReady) return undefined;

    if (drawMode === DRAW_MODES.POINTS) {
      detachPencilDraw();
      attachDrawClick();
    } else {
      detachMapClick();
      attachPencilDraw();
    }

    return () => {
      detachMapClick();
      detachPencilDraw();
    };
  }, [
    open,
    loading,
    mapReady,
    drawMode,
    attachDrawClick,
    attachPencilDraw,
    detachMapClick,
    detachPencilDraw,
  ]);

  useEffect(() => {
    if (!open || loading) return;
    renderMap();
  }, [open, loading, renderMap]);

  function handleGoogleApiLoaded({ map, maps, google }) {
    mapApiRef.current = { map, maps, google };
    setMapReady(true);
    if (initialMapViewport) {
      applyViewportToGoogleMap(map, initialMapViewport);
    }
    renderMap();
  }

  function handleSaveMapViewport() {
    const map = mapApiRef.current && mapApiRef.current.map;
    if (!map) return;
    const viewport = readViewportFromGoogleMap(map);
    if (!viewport) return;
    writeLocalMapViewport(
      MAP_VIEWPORT_SCOPES.SERVICE_ORDER_MANUAL_APPLICATION,
      farmId,
      viewport
    );
    toast.success(t("Posição do mapa salva."), { position: "bottom-right" });
  }

  function handleUndoDraftPoint() {
    setDraftPoints((current) => current.slice(0, -1));
  }

  function handleClearDraft() {
    setDraftPoints([]);
  }

  function handleAddPiece() {
    if (draftPoints.length < 3) {
      toast.error(t("Desenhe o perímetro com pelo menos 3 pontos."), {
        position: "bottom-right",
      });
      return;
    }
    const maps = mapApiRef.current && mapApiRef.current.maps;
    setSavedPieces((current) =>
      current.concat(buildPieceFromDraft(draftPoints, maps, current.length))
    );
    setDraftPoints([]);
  }

  function handleRemovePiece(pieceId) {
    setSavedPieces((current) =>
      current
        .filter((piece) => piece.id !== pieceId)
        .map((piece, index) => ({
          ...piece,
          label: `Trecho ${index + 1}`,
        }))
    );
  }

  function handleClose() {
    onClose();
    releaseModalBlockers();
  }

  function handleConfirm() {
    if (draftPoints.length >= 3) {
      toast.warn(
        t("Conclua o trecho em desenho ou limpe os pontos antes de confirmar."),
        { position: "bottom-right" }
      );
      return;
    }
    if (savedPieces.length === 0) {
      toast.error(t("Desenhe ao menos um trecho da aplicação."), {
        position: "bottom-right",
      });
      return;
    }
    onConfirm(savedPieces);
    handleClose();
  }

  const instruction =
    draftPoints.length < 3
      ? drawMode === DRAW_MODES.PENCIL
        ? t(
            "Arraste no mapa para desenhar o perímetro com o lápis (mínimo 3 pontos). Você pode adicionar vários trechos separados."
          )
        : t(
            "Clique no mapa para desenhar um trecho da aplicação (mínimo 3 pontos). Você pode adicionar vários trechos separados."
          )
      : `${t("Trecho atual")}: ${draftMetrics.areaHa.toFixed(2)} ha · ${draftMetrics.perimeterM.toFixed(0)} m`;

  return (
    <ServiceOrderMapOverlay
      open={open}
      onClose={handleClose}
      ariaLabelledBy="service-order-manual-application-map-title"
    >
      <div
        className="track-heading-map-modal service-order-location-map-modal service-order-manual-application-map-modal"
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
        }}
      >
        <ResizeHandles />

        <div className="track-heading-map-modal__header">
          <h2 id="service-order-manual-application-map-title">
            <FaDrawPolygon style={{ marginRight: 8 }} />
            {t("Desenhar perímetro da aplicação")}
          </h2>
          <button
            type="button"
            className="track-heading-map-modal__close"
            onClick={handleClose}
          >
            <FaTimes />
          </button>
        </div>

        <div className="service-order-location-map-modal__toolbar service-order-manual-application-map-modal__toolbar">
          <div className="service-order-manual-application-map-modal__draw-tools">
            <button
              type="button"
              className={`btn btn-sm btn-default ${
                drawMode === DRAW_MODES.POINTS ? "is-active" : ""
              }`}
              onClick={() => setDrawMode(DRAW_MODES.POINTS)}
              title={t("Marcar pontos no mapa")}
            >
              <FaMapPin style={{ marginRight: 6 }} />
              {t("Pontos")}
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-default ${
                drawMode === DRAW_MODES.PENCIL ? "is-active" : ""
              }`}
              onClick={() => setDrawMode(DRAW_MODES.PENCIL)}
              title={t("Desenhar livremente com o lápis")}
            >
              <FaPen style={{ marginRight: 6 }} />
              {t("Lápis")}
            </button>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={handleUndoDraftPoint}
            disabled={draftPoints.length === 0}
          >
            <FaUndo style={{ marginRight: 6 }} />
            {t("Desfazer ponto")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={handleClearDraft}
            disabled={draftPoints.length === 0}
          >
            {t("Limpar trecho atual")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={handleAddPiece}
            disabled={draftPoints.length < 3}
          >
            <FaPlus style={{ marginRight: 6 }} />
            {t("Adicionar trecho")}
          </button>
        </div>

        <p className="track-heading-map-modal__hint">{instruction}</p>

        {piecesSummary.manualPieceCount > 0 || draftMetrics ? (
          <div className="service-order-location-map-modal__summary">
            <strong>
              {piecesSummary.manualPieceCount} {t("trecho(s)")} ·{" "}
              {piecesSummary.manualHa.toFixed(2)} ha {t("total")}
            </strong>
            {draftMetrics ? (
              <div className="service-order-location-map-modal__summary-split">
                <span>
                  {t("Trecho em desenho")}: {draftMetrics.areaHa.toFixed(2)} ha
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        {savedPieces.length > 0 ? (
          <div className="service-order-manual-application-map-modal__pieces">
            {savedPieces.map((piece, index) => (
              <div
                key={piece.id}
                className="service-order-manual-application-map-modal__piece"
              >
                <span
                  className="service-order-manual-application-map-modal__piece-color"
                  style={{ background: manualPieceColor(index).fill }}
                />
                <span>
                  {piece.label}: {piece.areaHa.toFixed(2)} ha ·{" "}
                  {piece.perimeterM.toFixed(0)} m
                </span>
                <button
                  type="button"
                  className="btn btn-xs btn-default"
                  onClick={() => handleRemovePiece(piece.id)}
                  aria-label={t("Remover trecho")}
                >
                  <FaTrashAlt />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div className="track-heading-map-modal__map">
          {loading || !farmCenter ? (
            <div className="rumo-spinner">
              <MDSpinner />
            </div>
          ) : (
            <div className="rumo-map-wrapper track-heading-map-modal__map-inner service-order-manual-application-map-modal__map">
              <GoogleMapReact
                key={`service-order-manual-application-map-${farmId}`}
                bootstrapURLKeys={{ key: myConfig.GOOGLE_API_KEY }}
                options={getMapOptions}
                defaultCenter={
                  initialMapViewport
                    ? {
                        lat: initialMapViewport.lat,
                        lng: initialMapViewport.lng,
                      }
                    : { lat: farmCenter.ltd, lng: farmCenter.lng }
                }
                defaultZoom={initialMapViewport ? initialMapViewport.zoom : 16}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={handleGoogleApiLoaded}
              />
              <MapViewportToolbar
                onSave={handleSaveMapViewport}
                disabled={loading || !farmCenter}
                label={t("Salvar posição do mapa")}
              />
            </div>
          )}
        </div>

        <div className="service-order-location-map-modal__footer">
          <div className="service-order-manual-application-map-modal__footer-note">
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t(
              "Use vários trechos para aplicar só partes do talhão ou manchas em locais diferentes."
            )}
          </div>
          <div className="service-order-location-map-modal__footer-actions">
            <button type="button" className="btn btn-default" onClick={handleClose}>
              {t("Cancelar")}
            </button>
            <button
              type="button"
              className="service-order-location-map-modal__action-btn is-row is-primary"
              onClick={handleConfirm}
            >
              <FaCheck size={18} />
              <span>
                {t("Usar trechos desenhados")}
                {piecesSummary.manualPieceCount > 0
                  ? ` (${piecesSummary.manualPieceCount} · ${piecesSummary.manualHa.toFixed(2)} ha)`
                  : ""}
              </span>
            </button>
          </div>
        </div>
      </div>
    </ServiceOrderMapOverlay>
  );
}
